/**
 * PhotoManager — the seller's studio photo gallery editor.
 *
 * Presented as a modal on top of the seller dashboard, either:
 *   - immediately after upload (auto-opened on the fresh listing), or
 *   - from the listing management row's "Photos" action.
 *
 * Features:
 *   - Grid of all photos (current cover clearly ringed)
 *   - Set-as-cover (tap the star icon)
 *   - Delete with confirmation (min 1 photo enforced by backend, guarded here too)
 *   - Drag-reorder (react's HTML5 DnD, no extra deps)
 *   - Regenerate button (destructive — replaces auto/legacy set, keeps manual)
 *   - Add-from-viewer button (opens a live 3D preview + capture)
 *
 * Every action has an optimistic UI update; failures surface a toast and
 * refetch to correct state.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Star, Trash2, RefreshCw, Loader2, GripVertical, Upload, Rocket, AlertTriangle, Maximize2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import RenderProgressPanel from "@/components/RenderProgressPanel";
import ClientStudioRenderer from "@/components/ClientStudioRenderer";

const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function PhotoManager({ listing, onClose, onSaved }) {
  const [photos, setPhotos] = useState([]);
  const [meta, setMeta] = useState({
    can_delete_studio_count: 0,
    min_auto_remaining: 4,
    seller_upload_slots_left: 10,
    manual_count: 0,
    can_publish: false,
    publish_blockers: [],
    status: "pending",
  });
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [aiPicking, setAiPicking] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Live render-progress state — populated whenever a render is in
  // flight for this listing (either freshly launched or resumed on modal
  // reopen). Drives the RenderProgressPanel tile grid.
  const [progress, setProgress] = useState(null);
  const dragRef = useRef({ from: null });
  const fileInputRef = useRef(null);
  const pollTimerRef = useRef(null);
  const notifiedForJobRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get(`/seller/listings/${listing.id}/photos`)
      .then((r) => {
        setPhotos(r.data.photos || []);
        setMeta({
          can_delete_studio_count: r.data.can_delete_studio_count ?? 0,
          min_auto_remaining: r.data.min_auto_remaining ?? 4,
          seller_upload_slots_left: r.data.seller_upload_slots_left ?? 10,
          manual_count: r.data.manual_count ?? 0,
          can_publish: !!r.data.can_publish,
          publish_blockers: r.data.publish_blockers || [],
          status: r.data.status || "pending",
        });
      })
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [listing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------- live render-progress polling --------------
  // Kicked off in three ways:
  //   1. Modal opens on a listing that already has a running job (the
  //      seller closed the modal earlier — job kept running server-side).
  //   2. User clicks Regenerate (starts a new job).
  //   3. Modal is auto-opened right after upload (progress from the
  //      initial render).
  // Poll cadence 1s while running; stops the moment status leaves
  // {queued, running}. The interval is stored in a ref so we can cancel
  // it on modal unmount without needing to thread state around.
  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };
  const pollOnce = async () => {
    try {
      const { data } = await api.get(
        `/seller/listings/${listing.id}/render-progress`,
      );
      const job = data.job;
      setProgress(job);
      const stillRunning = data.running;
      if (!stillRunning) {
        setRegen(false);
        stopPolling();
        // Reload photos so the grid reflects any brand-new files.
        load();
        // Fire an in-app toast once per completed job so the user knows
        // even if they closed the modal (the /notifications feed also
        // gets the row via the backend `notify()` call in regenerate).
        if (job && notifiedForJobRef.current !== job.job_id) {
          notifiedForJobRef.current = job.job_id;
          if (job.status === "complete") {
            toast.success("Studio photos are ready.");
          } else if (job.status === "failed") {
            toast.error(job.error || "Rendering failed.");
          }
        }
        return;
      }
      // Reload photos every ~5 ticks so completed views show in the main
      // grid too (not just the progress tiles).
      pollTimerRef.current = setTimeout(pollOnce, 1000);
    } catch {
      // Transient — keep polling.
      pollTimerRef.current = setTimeout(pollOnce, 2000);
    }
  };
  const startPolling = () => {
    stopPolling();
    pollOnce();
  };
  useEffect(() => {
    // Auto-resume any job that was running when the modal reopened.
    (async () => {
      try {
        const { data } = await api.get(
          `/seller/listings/${listing.id}/render-progress`,
        );
        if (data.running) {
          setProgress(data.job);
          setRegen(true);
          startPolling();
        }
      } catch { /* ignore */ }
    })();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id]);

  const setCover = async (p) => {
    // Optimistic
    setPhotos((ps) => ps.map((x) => ({ ...x, is_cover: x.filename === p.filename })));
    try {
      await api.post(`/seller/listings/${listing.id}/photos/set-cover`, { filename: p.filename });
      toast.success("Cover updated");
    } catch (e) { toast.error(apiErr(e)); load(); }
  };

  const del = async (p) => {
    if (photos.length <= 1) {
      toast.error("At least one photo is required for your listing.");
      return;
    }
    // 2026-03-01 — Floor rule only applies to LEGACY `auto` photos
    // (server-rendered — expensive to re-produce). `client_render`
    // photos are cheap to regenerate in the browser and can be
    // deleted freely; treating them as blocked here was why sellers
    // saw "Cannot delete" on every studio photo — the counter
    // (`can_delete_studio_count`) correctly excludes client_render,
    // but this gate was mis-classifying them as auto.
    const isLegacyAuto = p.source === "auto";
    if (isLegacyAuto && meta.can_delete_studio_count <= 0) {
      toast.error(`At least ${meta.min_auto_remaining} studio photos must remain on your listing.`);
      return;
    }
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    // Optimistic
    setPhotos((ps) => ps.filter((x) => x.filename !== p.filename));
    try {
      const { data } = await api.delete(`/seller/listings/${listing.id}/photos/${p.filename}`);
      if (data.promoted_cover) {
        toast.success(`Deleted. New cover is set to the next photo.`);
      } else {
        toast.success("Photo deleted");
      }
      load();
    } catch (e) { toast.error(apiErr(e)); load(); }
  };

  // ---------------- drag reorder ----------------
  const onDragStart = (i) => (e) => { dragRef.current.from = i; e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (i) => (e) => { e.preventDefault(); };
  const onDrop = (i) => async (e) => {
    e.preventDefault();
    const from = dragRef.current.from;
    dragRef.current.from = null;
    if (from == null || from === i) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setPhotos(next.map((p, ord) => ({ ...p, order: ord })));
    try {
      await api.post(`/seller/listings/${listing.id}/photos/reorder`, {
        filenames_in_order: next.map((p) => p.filename),
      });
    } catch (e) { toast.error(apiErr(e)); load(); }
  };

  // 2026-02-28 — client-side regenerate. Triggering this state mounts
  // the <ClientStudioRenderer> panel inside the modal, which loads the
  // listing's STL/3DM in the browser and re-uploads the 8 views under
  // source=client_render. Any pre-existing client_render / auto photos
  // are deleted on the server first so the fresh batch replaces them.
  const [clientRender, setClientRender] = useState(null);
  // 2026-02-28 — click-to-zoom lightbox. Set to the clicked photo dict;
  // null closes. Renders above the PhotoManager modal via z-[10001] so
  // it stacks over both the modal (z-9998) and toast toaster (z-100000
  // pointer-none). Keyboard: Esc closes; arrow keys navigate.
  const [preview, setPreview] = useState(null);

  // Esc / arrow-key handling — closes and navigates the lightbox.
  useEffect(() => {
    if (!preview) return;
    const idx = photos.findIndex((p) => p.filename === preview.filename);
    const onKey = (e) => {
      if (e.key === "Escape") setPreview(null);
      else if (e.key === "ArrowRight" && idx >= 0 && idx < photos.length - 1) setPreview(photos[idx + 1]);
      else if (e.key === "ArrowLeft" && idx > 0) setPreview(photos[idx - 1]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview, photos]);

  const regenerate = async () => {
    if (!window.confirm(
      "Regenerate 8 studio photos from scratch in your browser? This replaces the current auto-generated set. Any photos you added manually will be kept."
    )) return;
    let lst;
    try {
      const { data } = await api.get(`/listings/${listing.id}`);
      lst = data;
    } catch (e) {
      return toast.error(apiErr(e));
    }
    if (!lst.stl_file && !lst.dm_file && !lst.stl_url && !lst.dm_url) {
      return toast.error("The CAD file for this listing is not available for re-rendering.");
    }
    // 2026-02-28 fix — clear stale photos AND immediately refresh the
    // grid so the seller doesn't think nothing's happening while the
    // 8-photo delete loop runs. Previously the grid stayed populated
    // during deletion, sellers thought the click was ignored, and
    // pressed Regenerate 2-3 more times.
    setRegen(true);
    try {
      const stale = photos.filter((p) => p.source === "client_render" || p.source === "auto");
      for (const p of stale) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await api.delete(`/seller/listings/${listing.id}/photos/${encodeURIComponent(p.filename)}`);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.info("[regenerate] delete stopped at floor:", e?.response?.data?.detail);
          break;
        }
      }
    } catch (e) { /* best effort */ }
    // Force an immediate photo reload so the grid empties visually
    // BEFORE we mount the browser renderer. Sellers now see the
    // 0-photo state + the "Rendering in your browser" panel together,
    // proving the click was received.
    await load();
    setClientRender({
      hasStl: !!(lst.stl_file || lst.stl_url),
      hasDm:  !!(lst.dm_file  || lst.dm_url),
    });
  };

  const publish = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/seller/listings/${listing.id}/publish`);
      if (data.already_published) {
        toast.info("This listing is already live.");
      } else {
        toast.success("Listing published — it's live on the marketplace.");
      }
      load();
      onSaved?.();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setPublishing(false);
    }
  };

  const cancelRender = async () => {
    if (cancelling) return;
    if (!window.confirm("Stop this render? The slot will free up for the next upload.")) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/seller/listings/${listing.id}/photos/cancel`);
      if (data.cancelled) {
        toast.success("Render cancelled — the slot is free.");
      } else {
        toast.info("No render was actually running.");
      }
      // Let one more poll settle the doc into the failed/cancelled state
      // so the panel switches copy before we tear regen down.
      setTimeout(() => setRegen(false), 500);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setCancelling(false);
    }
  };

  const aiPickCover = async () => {
    if (photos.length < 2) {
      toast.error("Need at least 2 photos for AI to pick from.");
      return;
    }
    setAiPicking(true);
    try {
      const { data } = await api.post(`/ai/pick-cover/${listing.id}`);
      // Reflect the new cover locally so the star instantly moves
      setPhotos((ps) => ps.map((p, i) => ({ ...p, is_cover: i === data.picked })));
      toast.success(`AI picked photo #${data.picked + 1} as your cover.`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setAiPicking(false); }
  };

  const onUploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const oversized = files.filter((f) => f.size > 8 * 1024 * 1024);
    if (oversized.length > 0) {
      toast.error(`${oversized.length} file${oversized.length > 1 ? "s were" : " was"} over 8 MB and skipped.`);
    }
    const valid = files.filter((f) => f.size <= 8 * 1024 * 1024);
    if (valid.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setUploading(true);
    let ok = 0, fail = 0;
    // Sequential loop — server-side moderation is per-file and we want
    // clear per-file error toasts. Loop is fast enough (~1-3 s per photo).
    for (const file of valid) {
      const form = new FormData();
      form.append("file", file);
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post(
          `/seller/listings/${listing.id}/photos/upload`, form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        setPhotos((ps) => [...ps, data.photo]);
        ok += 1;
      } catch (err) {
        toast.error(`"${file.name}": ${apiErr(err)}`);
        fail += 1;
      }
    }
    load();
    if (ok > 0 && fail === 0) {
      toast.success(`${ok} photo${ok > 1 ? "s" : ""} added to your gallery.`);
    } else if (ok > 0 && fail > 0) {
      toast.warning(`${ok} added, ${fail} rejected — see per-file errors above.`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="photo-manager"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto flex flex-col">
        {/* --- header --- */}
        <div className="px-5 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-display font-bold text-lg">Studio photos</h3>
            <p className="text-xs text-muted-foreground truncate max-w-md">
              {listing?.title || listing?.id}
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-secondary" data-testid="photo-close"><X className="h-4 w-4" /></button>
        </div>

        {/* --- toolbar --- */}
        <div className="px-5 py-3 border-b border-border bg-secondary/30 flex flex-wrap gap-2 items-center justify-between">
          <p className="text-[11px] text-muted-foreground font-mono">
            {photos.length}/{meta.max_photos || 10} photo{photos.length !== 1 ? "s" : ""} · tap the star to set cover · drag to reorder
          </p>
          <div className="flex gap-2 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={onUploadPhoto}
              className="hidden"
              data-testid="photo-upload-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="photo-upload-btn"
              title="Upload your own photo (JPEG, PNG or WebP, up to 8 MB)"
              className="inline-flex items-center gap-1.5 border-2 border-primary/70 bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-[12px] font-bold shadow-sm hover:bg-primary/90 disabled:opacity-60"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Upload my own photo
            </button>
            {/* 2026-02: AI-pick-cover REMOVED from the single-listing modal
                on user's directive. Sellers now decide the cover themselves
                by tapping the star. Bulk upload still uses the AI picker
                because per-listing manual selection isn't practical there. */}
            <button
              onClick={regenerate}
              disabled={regen}
              data-testid="photo-regenerate"
              className="inline-flex items-center gap-1.5 border border-amber-500/40 text-amber-600 rounded-md px-3 py-1.5 text-[12px] font-semibold hover:bg-amber-500/5 disabled:opacity-60"
            >
              {regen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Regenerate {Math.min(8, Math.max(0, (meta.max_photos || 10) - (meta.manual_count || 0)))} photos
            </button>
            {meta.status !== "approved" ? (
              <button
                onClick={publish}
                disabled={publishing || !meta.can_publish || photos.length === 0}
                data-testid="photo-publish-btn"
                title={
                  photos.length === 0
                    ? "Upload at least one of your own render images to publish."
                    : meta.can_publish
                      ? "Make this listing live on the marketplace"
                      : (meta.publish_blockers?.[0] || "Publish is blocked — see the banner")
                }
                className="inline-flex items-center gap-1.5 border-2 rounded-md px-3 py-1.5 text-[12px] font-bold shadow-sm disabled:opacity-40 disabled:cursor-not-allowed border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700"
              >
                {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
                Publish listing
              </button>
            ) : null}
          </div>
        </div>

        {/* --- grid --- */}
        <div className="p-5 flex-1 overflow-y-auto">
          {/* Live render-progress panel — visible ONLY while a render is
              actually in flight (status queued or running). Previously
              we also rendered it for status=failed, which meant a
              cancelled/errored job from a previous session kept
              showing "Generating photo 1 of 8 · Preparing mesh · 0%"
              on modal reopen. Failure copy is already carried by the
              blocker banner below. */}
          {progress && (progress.status === "queued" || progress.status === "running") ? (
            <div className="mb-4" data-testid="pm-render-progress-wrap">
              <RenderProgressPanel
                job={progress}
                imgBase={IMG_BASE}
                onCancel={cancelRender}
                cancelling={cancelling}
              />
            </div>
          ) : null}
          {/* 2026-02-28 — client-side regenerate panel. Mounted when the
              seller clicks Regenerate 8 photos; unmounts on complete or
              on failure (in which case the amber banner above surfaces
              the manual-upload path automatically). */}
          {clientRender && (
            <div className="mb-4" data-testid="pm-client-render-wrap">
              <ClientStudioRenderer
                listingId={listing.id}
                hasStl={clientRender.hasStl}
                hasDm={clientRender.hasDm}
                maxViews={Math.max(0, (meta.max_photos || 10) - (meta.manual_count || 0))}
                onProgress={() => {
                  // Refresh the photo grid as each view lands so the
                  // seller sees photos appearing in real-time, not all
                  // at once at the end. Fixes the "did I click it?"
                  // regenerate-multiple-times confusion.
                  load();
                }}
                onComplete={() => {
                  toast.success("Studio photos regenerated in your browser.");
                  setClientRender(null);
                  setRegen(false);
                  load();
                }}
                onFailure={(reason) => {
                  toast.warning("Your browser couldn't regenerate the studio photos — please upload your own.");
                  // eslint-disable-next-line no-console
                  console.warn("[PhotoManager regenerate] client render failed:", reason);
                  setClientRender(null);
                  setRegen(false);
                  load();
                }}
              />
            </div>
          )}
          {/* 2026-02 forced-cover wizard indicator — shown whenever the
              listing is still held (status != approved). Walks the seller
              through Step 1 Own-render → Step 2 Cover → Step 3 Publish,
              lighting up each step as the underlying gate clears. Keeps
              the "you can't skip cover" rule visually front-and-centre. */}
          {meta.status !== "approved" ? (() => {
            const manualDone = meta.manual_count >= 1;
            const coverDone = !meta.publish_blockers?.some(b => /cover/i.test(b));
            // Step 1 auto-succeeds when auto photos exist (skippable)
            // and requires manualDone otherwise (mandatory).
            const autoRendered = photos.some(p => p.source === "auto");
            const step1Done = autoRendered || manualDone;
            const step2Done = coverDone;
            const currentStep = !step1Done ? 1 : !step2Done ? 2 : 3;
            const Step = ({ n, label, done, active }) => (
              <div className={`flex items-center gap-2 ${active ? "font-bold" : ""}`}>
                <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                  done ? "bg-emerald-600 text-white" :
                  active ? "bg-primary text-primary-foreground ring-2 ring-primary/30" :
                  "bg-slate-200 text-slate-500"
                }`}>{done ? "✓" : n}</div>
                <span className={`text-[11px] uppercase tracking-widest ${done ? "text-emerald-700" : active ? "text-primary" : "text-slate-500"}`}>
                  {label}
                </span>
              </div>
            );
            return (
              <div className="mb-4 rounded-md border border-primary/20 bg-primary/[0.03] px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="pm-wizard-steps">
                <Step n={1} label={autoRendered ? "Own render (optional)" : "Own render (required)"} done={step1Done} active={currentStep === 1} />
                <span className="text-slate-300">→</span>
                <Step n={2} label="Pick cover" done={step2Done} active={currentStep === 2} />
                <span className="text-slate-300">→</span>
                <Step n={3} label="Publish" done={false} active={currentStep === 3} />
              </div>
            );
          })() : null}
          {/* 2026-02 seller-photo mandate banner. Shows the first blocker
              (either "upload at least one of your own render images" or
              "pick a cover") until the listing is publish-ready. Hidden
              once the listing is already live. */}
          {meta.status !== "approved" && meta.publish_blockers?.length ? (
            <div
              className="mb-4 rounded-md border border-amber-500/40 bg-amber-50/70 p-3 flex items-start gap-2"
              data-testid="publish-blocker-banner"
            >
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-amber-800 leading-snug font-medium">
                {meta.publish_blockers[0]}
              </p>
            </div>
          ) : null}
          {!loading && (
            <div
              className="mb-3 flex items-center justify-between text-xs font-mono text-muted-foreground"
              data-testid="photo-quota-row"
            >
              {photos.length > 0 ? (
                <span data-testid="photo-delete-counter">
                  {meta.can_delete_studio_count > 0
                    ? `You can delete ${meta.can_delete_studio_count} more studio photo${meta.can_delete_studio_count === 1 ? "" : "s"}.`
                    : `At least ${meta.min_auto_remaining} studio photos must remain on your listing.`}
                </span>
              ) : (
                <span className="text-destructive font-bold" data-testid="photo-empty-hint">
                  No auto photos — upload at least one of your own to continue.
                </span>
              )}
              <span
                data-testid="photo-upload-slots"
                className={meta.manual_count === 0 ? "text-destructive font-bold" : ""}
              >
                Your uploads: {meta.manual_count}/{meta.max_photos || 10}
                {meta.manual_count === 0 ? " (required)" : ""}
                {meta.studio_slots_left === 0 && meta.manual_count > 0
                  ? " · gallery full, new uploads replace a studio render"
                  : ""}
              </span>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading photos…
            </div>
          ) : photos.length === 0 ? (
            <div className="border border-dashed border-border rounded-md py-16 text-center text-muted-foreground">
              No photos yet — click <b>Regenerate</b> to run the studio pipeline.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map((p, i) => (
                <div
                  key={p.filename}
                  className={`relative group rounded-md overflow-hidden border-2 bg-white transition-all ${p.is_cover ? "border-primary shadow-lg ring-2 ring-primary/30" : "border-border hover:border-primary/50"}`}
                  draggable
                  onDragStart={onDragStart(i)}
                  onDragOver={onDragOver(i)}
                  onDrop={onDrop(i)}
                  data-testid={`photo-thumb-${i}`}
                >
                  <img
                    src={`${IMG_BASE}/api/media/images/${p.filename}?w=240`}
                    alt={p.view || `Photo ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    onClick={() => setPreview(p)}
                    data-testid={`photo-open-${i}`}
                    className="w-full aspect-square object-cover bg-slate-50 cursor-zoom-in"
                  />
                  {/* corner badges */}
                  <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
                    <span className="bg-slate-900/80 text-white text-[9px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5 flex items-center gap-0.5">
                      <GripVertical className="h-2.5 w-2.5" /> {i + 1}
                    </span>
                    {p.is_cover && (
                      <span className="bg-primary text-primary-foreground text-[9px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5">cover</span>
                    )}
                    {p.source === "manual" && (
                      <span
                        className="bg-emerald-600 text-white text-[9px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5"
                        data-testid={`photo-your-upload-badge-${i}`}
                      >
                        Your upload
                      </span>
                    )}
                  </div>
                  {/* hover actions */}
                  <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => setPreview(p)}
                      title="Open full-size preview"
                      data-testid={`photo-zoom-${i}`}
                      className="bg-white hover:bg-primary hover:text-white rounded-md p-2 shadow"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </button>
                    {!p.is_cover && (
                      <button
                        onClick={() => setCover(p)}
                        title="Set as cover"
                        data-testid={`photo-set-cover-${i}`}
                        className="bg-white hover:bg-primary hover:text-white rounded-md p-2 shadow"
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    {(() => {
                      // 2026-03-01 — Floor rule is scoped to legacy
                      // `auto` (server-rendered) photos only, matching
                      // the backend. `client_render` photos always
                      // delete freely — see `del()` above for details.
                      const isLegacyAuto = p.source === "auto";
                      const blockedByFloor = isLegacyAuto && meta.can_delete_studio_count <= 0;
                      const disabled = photos.length <= 1 || blockedByFloor;
                      const title = photos.length <= 1
                        ? "At least one photo required"
                        : blockedByFloor
                          ? `At least ${meta.min_auto_remaining} studio photos must remain on your listing.`
                          : "Delete photo";
                      return (
                        <button
                          onClick={() => del(p)}
                          disabled={disabled}
                          title={title}
                          data-testid={`photo-delete-${i}`}
                          className="bg-white hover:bg-red-500 hover:text-white rounded-md p-2 shadow disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      );
                    })()}
                  </div>
                  <div className="px-2 py-1.5 text-[10px] text-muted-foreground font-mono uppercase tracking-wider truncate">
                    {p.view || "photo"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* --- footer --- */}
        {/* 2026-02-28 — "Done" button removed. Sellers were mistaking
            it for a "Publish listing" action. The only path to live is
            the explicit Publish button higher up in this modal; the X
            in the top-right closes without publishing. */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between sticky bottom-0 bg-white">
          <p className="text-[11px] text-muted-foreground">
            {photos.length <= 1
              ? "At least 1 photo is required."
              : "Changes save instantly · use the Publish button above to go live."}
          </p>
          <button
            onClick={() => { onSaved && onSaved(); onClose(); }}
            data-testid="photo-close"
            className="text-sm font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground px-3 py-1.5"
          >
            Close
          </button>
        </div>
      </div>

      {/* 2026-02-28 — click-to-zoom lightbox. Layers ABOVE the modal via
          z-[10001] (modal is z-[9998]) and above the toast toaster
          (z-[100000] but pointer-events-none). Backdrop click closes;
          Esc / ← / → also handled via the useEffect above. */}
      {preview && (
        <div
          className="fixed inset-0 z-[10001] bg-black/85 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="photo-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setPreview(null); }}
            className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 text-white p-2"
            data-testid="photo-lightbox-close"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={`${IMG_BASE}/api/media/images/${preview.filename}`}
            alt={preview.view || "Photo preview"}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
            data-testid="photo-lightbox-img"
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 text-white text-xs font-mono uppercase tracking-widest">
            <span>{preview.view || "photo"}</span>
            {preview.is_cover && (
              <span className="bg-primary text-primary-foreground rounded px-2 py-0.5">Cover</span>
            )}
            {preview.source === "manual" && (
              <span className="bg-emerald-600 text-white rounded px-2 py-0.5">Your upload</span>
            )}
            {preview.source === "client_render" && (
              <span className="bg-slate-700 text-white rounded px-2 py-0.5">Studio render</span>
            )}
            <span className="text-white/60">
              {photos.findIndex((p) => p.filename === preview.filename) + 1} / {photos.length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
